import { useState } from "react";
import {
  Compass, Radio, Network, Brain, Sparkles, Send, Rocket, ShieldCheck,
  Target, BookOpen, Search, GitBranch, FileSearch, Coins, Handshake,
  ListChecks, Users, Layers, Eye, ArrowRight, Building2,
} from "lucide-react";

const PHASES = [
  {
    n: "0", key: "calibrer", title: "Se calibrer", tagline: "Qui vous êtes",
    color: "#8B7BE8", icon: Compass,
    desc: "Avant tout signal, Noam apprend votre métier et vos règles de décision. C'est ce qui rend chaque information pertinente pour VOUS.",
    modules: [
      ["Positionnement", "Métier, secteurs, zones cibles, écosystème — la source de vérité du croisement."],
      ["Doctrine 51", "Vos principes de décision, injectés dans chaque analyse IA."],
      ["Ontologie", "Le vocabulaire et les types d'entités propres à votre monde."],
      ["Configurer", "Documents stratégiques de référence."],
    ],
  },
  {
    n: "1", key: "capter", title: "Capter", tagline: "Le monde entre",
    color: "#378ADD", icon: Radio,
    desc: "Les capteurs qui alimentent Noam en signaux faibles et forts, du terrain réel.",
    modules: [
      ["Noam Radar", "Veille des entités de votre écosystème (rachats, mouvements, presse)."],
      ["Radar › Défense", "Événements de défense mondiale (APITube)."],
      ["Radar › Succession PME", "PME suisses à céder + faillites d'entreprises (FOSC/SHAB)."],
      ["Immobilier › Permis", "Permis de construire suisses (SITG GE/VD/FR/NE)."],
      ["Marchés financiers", "Signaux de marché (multi-marchés)."],
      ["News / Miroir", "Actualité filtrée + email quotidien."],
      ["Analyses · URL Scanner · Documents", "Import et lecture de documents, pages et fichiers."],
    ],
  },
  {
    n: "2", key: "croiser", title: "Croiser", tagline: "Le cœur de Noam",
    color: "#EF9F27", icon: Network, highlight: true,
    desc: "Un signal seul ne vaut rien. Un signal qui croise votre position devient une opportunité. C'est ce qui distingue Noam d'un simple agrégateur de veille.",
    modules: [
      ["Annuaire", "Votre base d'entités — le pivot de tous les croisements."],
      ["Moteur « Opportunités détectées »", "Croise Radar + Permis + Deals + Annuaire + Positionnement → deals causals."],
      ["Score « chaleur »", "Note chaque cible selon sa proximité avec votre position."],
      ["Utility Mapping · Graphe", "Cartographie des acteurs, centralités, goulots."],
      ["Chaînage", "Cartographie causale Pourquoi → Comment ; le levier le plus puissant."],
      ["Asymétries", "Matrices de dispersion clientèle / marché."],
      ["Problem / Operational Gravity", "Hiérarchise problèmes lourds et piliers exposés."],
    ],
  },
  {
    n: "3", key: "analyser", title: "Analyser", tagline: "Donner du sens",
    color: "#1D9E75", icon: Sparkles,
    desc: "Le renseignement croisé devient lecture stratégique, chiffrée et défendable en comité.",
    modules: [
      ["IA & Assistants", "Raisonnement ancré doctrine, sorties propres board-ready."],
      ["Executive (systémique)", "Synthèse : problèmes, acteurs centraux, piliers fragiles."],
      ["Score", "Note de pilotage globale + piliers."],
      ["Simulateur · Avant-Après · Historique", "Scénarios, trajectoires, mémoire décisionnelle."],
      ["Lectures IA des modules", "Diagnostic + points d'attention + action concrète."],
    ],
  },
  {
    n: "4", key: "diffuser", title: "Diffuser", tagline: "Le livrable transférable",
    color: "#378ADD", icon: Send,
    desc: "Le renseignement doit circuler : board, clients, pairs. Tout est calibré « propre et transférable ».",
    modules: [
      ["Rapport Executive", "Rapport 1 page prêt pour un Conseil d'Administration."],
      ["Bibliothèque de briefs", "Briefs réutilisables."],
      ["Email quotidien · Digests", "Diffusion automatique filtrée."],
      ["Cartographies 360°", "Exports visuels et PDF."],
      ["Le Cercle", "Partage entre membres (opportunités, capital, expertise)."],
    ],
  },
  {
    n: "5", key: "agir", title: "Agir & suivre", tagline: "La boucle se ferme",
    color: "#E24B4A", icon: Rocket,
    desc: "Ici Noam dépasse tous les outils de veille : le signal devient acte tracé et suivi dans le temps.",
    modules: [
      ["Leads", "Le pipeline d'entrée. On y convertit un deal ou un acteur en un clic."],
      ["Origination · Capital · Financement", "Pipeline de deals et de capital."],
      ["Négociation · Courtage", "Structuration des négociations et mandats."],
      ["Plans d'action", "Les décisions deviennent des tâches datées et suivies."],
      ["Contrôles · Audits · Incidents · Alertes · Fournisseurs", "Exécution opérationnelle et conformité."],
      ["Gouvernance · Risques · RH", "Maîtrise des risques et du capital humain."],
    ],
  },
];

const BRIDGES = [
  "Deal Succession PME → Convertir en Lead",
  "Acteur du Positionnement → Convertir en Lead",
  "Capture de conscience → Plan d'action IA → Plan suivi (avec échéance)",
];

const LAYERS = [
  { title: "La Conscience", icon: Eye, color: "#378ADD",
    text: "Synchronisation de conscience (journal → plan d'action → lead), Aujourd'hui (module du jour), Dashboard. Noam garde une trace vivante et vous ramène vers l'action." },
  { title: "Méta-Intelligence", icon: Brain, color: "#8B7BE8",
    text: "Métacognition : Noam évalue sa propre cohérence, ses angles morts, la qualité de son raisonnement. L'intelligence qui se regarde penser." },
  { title: "Tour de contrôle (Ultra Admin)", icon: Building2, color: "#EF9F27",
    text: "Organisations, Plans, Utilisateurs, Demandes d'accès, Activité, Consommation IA, Observabilité, Data layer, Emails hebdo, Trend global. Le poste de pilotage de la plateforme." },
];

export default function CommentFonctionneNoam() {
  const [active, setActive] = useState("croiser");
  const current = PHASES.find((p) => p.key === active) || PHASES[0];

  return (
    <div className="max-w-6xl mx-auto space-y-10 pb-16" data-testid="comment-fonctionne-noam">
      {/* Hero */}
      <header className="pt-4">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#378ADD] font-semibold mb-4">
          <ShieldCheck className="w-3.5 h-3.5" /> Document fondateur · Architecture
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold text-white leading-[1.05]">
          Du signal <span className="text-[#EF9F27]">à la décision</span>
        </h1>
        <p className="text-base text-zinc-300 mt-4 max-w-2xl leading-relaxed">
          Noam n'est pas un tableau de bord de plus. C'est une <strong className="text-white">chaîne
          de renseignement complète</strong> : elle capte les signaux du marché, les croise avec
          votre position, les transforme en analyse défendable, puis en décisions suivies.
        </p>
        <p className="text-sm text-zinc-500 mt-3 max-w-2xl italic">
          La plupart des outils s'arrêtent à « capter et afficher ». Noam va jusqu'à l'acte.
        </p>
      </header>

      {/* Cycle stepper */}
      <section>
        <div className="flex flex-wrap items-stretch gap-2">
          {PHASES.map((p, i) => {
            const Icon = p.icon;
            const isActive = p.key === active;
            return (
              <div key={p.key} className="flex items-stretch gap-2">
                <button
                  type="button"
                  onClick={() => setActive(p.key)}
                  data-testid={`phase-tab-${p.key}`}
                  className={`group relative flex flex-col items-start text-left rounded-xl border px-4 py-3 min-w-[130px] transition-all ${
                    isActive ? "border-transparent shadow-lg scale-[1.02]" : "border-white/10 hover:border-white/25"
                  }`}
                  style={isActive ? { background: `${p.color}18`, borderColor: `${p.color}66` } : {}}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold"
                      style={{ background: `${p.color}22`, color: p.color }}
                    >
                      {p.n}
                    </span>
                    <Icon className="w-4 h-4" style={{ color: p.color }} />
                  </div>
                  <span className="text-sm font-semibold text-white">{p.title}</span>
                  <span className="text-[10px] text-zinc-500">{p.tagline}</span>
                </button>
                {i < PHASES.length - 1 && (
                  <div className="flex items-center text-zinc-700">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Active phase detail */}
      <section
        className="rounded-2xl border p-6 sm:p-8 transition-all"
        style={{ background: `${current.color}0D`, borderColor: `${current.color}33` }}
        data-testid="phase-detail"
      >
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${current.color}22`, color: current.color }}
          >
            <current.icon className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold" style={{ color: current.color }}>
                ÉTAPE {current.n}
              </span>
              {current.highlight && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${current.color}22`, color: current.color }}>
                  Le différenciateur
                </span>
              )}
            </div>
            <h2 className="text-2xl font-semibold text-white mt-0.5">{current.title}</h2>
            <p className="text-sm text-zinc-300 mt-2 max-w-2xl leading-relaxed">{current.desc}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-6">
          {current.modules.map(([name, role]) => (
            <div key={name} className="rounded-xl bg-black/20 border border-white/10 p-4">
              <div className="text-sm font-semibold text-white flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: current.color }} />
                {name}
              </div>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{role}</p>
            </div>
          ))}
        </div>

        {current.key === "agir" && (
          <div className="mt-6 rounded-xl border border-[#E24B4A]/30 bg-[#E24B4A]/[0.06] p-4">
            <div className="text-[11px] uppercase tracking-wider text-[#E24B4A] font-semibold mb-2">
              Les ponts qui ferment la boucle
            </div>
            <ul className="space-y-1.5">
              {BRIDGES.map((b) => (
                <li key={b} className="text-sm text-zinc-200 flex items-center gap-2">
                  <ArrowRight className="w-3.5 h-3.5 text-[#E24B4A] shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Transversal layers */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-white">Les couches transversales</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {LAYERS.map((l) => {
            const Icon = l.icon;
            return (
              <div key={l.title} className="noam-card p-5">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${l.color}1A`, color: l.color }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-sm font-semibold text-white">{l.title}</div>
                <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{l.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Closing statement */}
      <section className="rounded-2xl border border-[#378ADD]/30 bg-gradient-to-br from-[#378ADD]/10 to-transparent p-8 text-center">
        <p className="text-lg sm:text-xl text-white font-medium leading-relaxed max-w-3xl mx-auto">
          Noam capte les signaux du marché, les croise avec votre position, les transforme en analyse
          défendable, puis en décisions suivies — <span className="text-[#EF9F27]">l'intelligence
          économique qui va du signal à la décision.</span>
        </p>
        <p className="text-xs text-zinc-500 mt-4 max-w-xl mx-auto">
          Chaque module n'est pas une fonctionnalité isolée : c'est une étape d'une même chaîne.
          La valeur ne naît pas d'un module, mais de leur enchaînement.
        </p>
      </section>
    </div>
  );
}
